import { maybe, Maybe } from '@passionware/monads';

/** URL-derived scope — slugs from route / settings paths. */
export type AppScope =
  | { level: 'organization'; organizationId: string }
  | { level: 'workspace'; organizationId: string; workspaceSlug: string }
  | {
      level: 'team';
      organizationId: string;
      workspaceSlug: string;
      teamSlug: string;
    };

/** Resolved scope — numeric ids for APIs plus slugs for URLs. */
export type ResolvedAppScope =
  | { level: 'organization'; organizationId: string }
  | {
      level: 'workspace';
      organizationId: string;
      workspaceId: number;
      workspaceSlug: string;
    }
  | {
      level: 'team';
      organizationId: string;
      workspaceId: number;
      workspaceSlug: string;
      teamId: number;
      teamSlug: string;
    };

/**
 * Id-only browsing scope for services / API filters (no slugs).
 * Derive from {@link ResolvedAppScope} via {@link appScopeUtils.identityFromResolved}.
 */
export type AppScopeIdentity =
  | { level: 'organization'; organizationId: string }
  | { level: 'workspace'; organizationId: string; workspaceId: number }
  | {
      level: 'team';
      organizationId: string;
      workspaceId: number;
      teamId: number;
    };

export type AppScopeIdentityAt<L extends AppScopeIdentity['level']> = Extract<
  AppScopeIdentity,
  { level: L }
>;

export type StoredAppScope = AppScope;

const SCOPE_RANK: Record<AppScopeIdentity['level'], number> = {
  organization: 0,
  workspace: 1,
  team: 2
};

export const appScopeUtils = {
  identityFromResolved(scope: ResolvedAppScope): AppScopeIdentity {
    if (scope.level === 'organization') {
      return {
        level: 'organization',
        organizationId: scope.organizationId
      };
    }
    if (scope.level === 'workspace') {
      return {
        level: 'workspace',
        organizationId: scope.organizationId,
        workspaceId: scope.workspaceId
      };
    }
    return {
      level: 'team',
      organizationId: scope.organizationId,
      workspaceId: scope.workspaceId,
      teamId: scope.teamId
    };
  },

  /**
   * Drop to a parent (or same) browsing level and narrow the identity union.
   * Never raises (e.g. workspace → team).
   */
  lowerTo<L extends 'organization' | 'workspace'>(
    level: L,
    scope: AppScopeIdentity
  ): AppScopeIdentityAt<L> {
    if (SCOPE_RANK[scope.level] < SCOPE_RANK[level]) {
      throw new Error(
        `Cannot raise AppScopeIdentity from ${scope.level} to ${level}`
      );
    }
    if (level === 'organization') {
      return {
        level: 'organization',
        organizationId: scope.organizationId
      } as AppScopeIdentityAt<L>;
    }
    if (scope.level === 'organization') {
      throw new Error('Cannot lower organization scope to workspace');
    }
    return {
      level: 'workspace',
      organizationId: scope.organizationId,
      workspaceId: scope.workspaceId
    } as AppScopeIdentityAt<L>;
  },

  /** Identity for `useCan` / flags from a loaded team row (no AppScopeProvider). */
  identityFromTeam(
    organizationId: string,
    team: { id: number; workspaceId: number }
  ): AppScopeIdentity {
    return {
      level: 'team',
      organizationId,
      workspaceId: team.workspaceId,
      teamId: team.id
    };
  },

  /** Layer 3 — identity from org + stored entity workspace id (null → org-level). */
  identityFromEntityWorkspace(
    organizationId: string,
    enterpriseWorkspaceId: number | null | undefined
  ): AppScopeIdentity {
    if (enterpriseWorkspaceId != null) {
      return {
        level: 'workspace',
        organizationId,
        workspaceId: enterpriseWorkspaceId
      };
    }
    return { level: 'organization', organizationId };
  },

  /**
   * Layer 3 — the level a stored entity reads its settings at, as the backend resolves them:
   * its team when it has one inside a workspace, else its workspace, else the organization.
   */
  identityFromEntityScope(
    organizationId: string,
    enterpriseWorkspaceId: number | null | undefined,
    enterpriseTeamId: number | null | undefined
  ): AppScopeIdentity {
    if (enterpriseWorkspaceId != null && enterpriseTeamId != null) {
      return {
        level: 'team',
        organizationId,
        workspaceId: enterpriseWorkspaceId,
        teamId: enterpriseTeamId
      };
    }
    return appScopeUtils.identityFromEntityWorkspace(
      organizationId,
      enterpriseWorkspaceId
    );
  },

  getWorkspaceId(scope: AppScopeIdentity): Maybe<number> {
    if (scope.level === 'organization') {
      return maybe.ofAbsent();
    }
    return maybe.of(scope.workspaceId);
  },

  getWorkspaceSlug(scope: AppScope | ResolvedAppScope): Maybe<string> {
    if (scope.level === 'organization') {
      return maybe.ofAbsent();
    }
    return scope.workspaceSlug;
  },

  getTeamSlug(scope: AppScope | ResolvedAppScope): Maybe<string> {
    if (scope.level !== 'team') {
      return maybe.ofAbsent();
    }
    return scope.teamSlug;
  },

  /** Team identity for team-defined settings; absent outside a team scope. */
  getTeamIdentity(scope: ResolvedAppScope): Maybe<AppScopeIdentityAt<'team'>> {
    if (scope.level !== 'team') {
      return maybe.ofAbsent();
    }
    return {
      level: 'team',
      organizationId: scope.organizationId,
      workspaceId: scope.workspaceId,
      teamId: scope.teamId
    };
  },

  assertValid(scope: AppScope): void {
    if (!scope.organizationId) {
      throw new Error('AppScope requires organizationId');
    }
    if (scope.level === 'workspace' && !scope.workspaceSlug) {
      throw new Error('workspace scope requires workspaceSlug');
    }
    if (scope.level === 'team') {
      if (!scope.workspaceSlug) {
        throw new Error('team scope requires workspaceSlug');
      }
      if (!scope.teamSlug) {
        throw new Error('team scope requires teamSlug');
      }
    }
  },

  isEnterpriseScopedPath(pathname: string): boolean {
    return /\/w\/[^/]+/.test(pathname);
  },

  equals(a: AppScope, b: AppScope): boolean {
    if (a.level !== b.level || a.organizationId !== b.organizationId) {
      return false;
    }
    if (a.level === 'organization') {
      return true;
    }
    if (a.level === 'workspace' && b.level === 'workspace') {
      return a.workspaceSlug === b.workspaceSlug;
    }
    if (a.level === 'team' && b.level === 'team') {
      return a.workspaceSlug === b.workspaceSlug && a.teamSlug === b.teamSlug;
    }
    return false;
  }
};
